import React, { createContext, useContext, useState, useCallback } from 'react';

interface ContentLoadingContextType {
    isLoading: boolean;
    showLoading: () => void;
    hideLoading: () => void;
}

const ContentLoadingContext = createContext<ContentLoadingContextType | null>(null);

export function ContentLoadingProvider({ children }: { children: React.ReactNode }) {
    const [isLoading, setIsLoading] = useState(false);

    const showLoading = useCallback(() => setIsLoading(true), []);
    const hideLoading = useCallback(() => setIsLoading(false), []);

    return (
        <ContentLoadingContext.Provider value={{ isLoading, showLoading, hideLoading }}>
            {children}
        </ContentLoadingContext.Provider>
    );
}

export function useContentLoading() {
    const context = useContext(ContentLoadingContext);
    if (!context) {
        throw new Error('useContentLoading must be used within a ContentLoadingProvider');
    }
    return context;
}

// Global references for use outside React components (like ApiService)
let globalContentShowLoading: (() => void) | null = null;
let globalContentHideLoading: (() => void) | null = null;

export function setGlobalContentLoadingFunctions(show: () => void, hide: () => void) {
    globalContentShowLoading = show;
    globalContentHideLoading = hide;
}

export function showContentLoading() {
    globalContentShowLoading?.();
}

export function hideContentLoading() {
    globalContentHideLoading?.();
}

export { ContentLoadingContext };
